import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Convert any axios error into a human-readable string.
// Exported so pages can do `toast.error(formatApiError(e))` instead of
// the generic "Failed to load" / "Failed to update" that hides the real cause.
export function formatApiError(error, fallback = 'Request failed') {
  if (!error) return fallback;
  if (error?.code === 'ERR_NETWORK') {
    return 'Network error — server unreachable. Check your connection.';
  }
  const status = error?.response?.status;
  const data = error?.response?.data;
  let detail = '';
  if (typeof data === 'string') {
    detail = data;
  } else if (data) {
    // FastAPI shapes: {detail: "msg"} or {detail: [{loc, msg, type}, …]} or {error, message}
    if (typeof data.detail === 'string') detail = data.detail;
    else if (Array.isArray(data.detail)) detail = data.detail.map(d => `${(d.loc || []).slice(-1)[0]}: ${d.msg}`).join('; ');
    else if (data.message) detail = String(data.message);
    else if (data.error) detail = String(data.error);
  }
  if (status === 401) return 'Session expired — please log in again.';
  if (status === 403) return `Forbidden${detail ? `: ${detail}` : ''} — your account lacks the required role.`;
  if (status === 404) return `Not found${detail ? `: ${detail}` : ''}`;
  if (status === 422) return `Invalid input${detail ? `: ${detail}` : ''}`;
  if (status >= 500) return `Server error (${status})${detail ? `: ${detail}` : ''}`;
  if (status) return `${fallback} (HTTP ${status})${detail ? ` — ${detail}` : ''}`;
  return fallback;
}

// Handle auth errors + attach a user-friendly message to every rejection.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Stamp a ready-to-display message so callers can do `toast.error(e.userMessage)`
    try { error.userMessage = formatApiError(error); } catch { /* never throw from interceptor */ }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  getMe: () => api.get('/auth/me'),
};

export const settingsAPI = {
  getDemoMode: () => api.get('/settings/demo-mode'),
  setDemoMode: (visible) => api.put('/settings/demo-mode', { visible }),
  deleteDemoData: () => api.delete('/settings/demo-data'),
};

export const clientsAPI = {
  getAll: () => api.get('/clients'),
  getById: (id) => api.get(`/clients/${id}`),
  getDetail: (id) => api.get(`/clients/${id}/detail`),
  create: (data) => api.post('/clients', data),
  getDocuments: (id) => api.get(`/clients/${id}/documents`),
  uploadDocument: (id, formData) => api.post(`/clients/${id}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (clientId, docId) => api.delete(`/clients/${clientId}/documents/${docId}`),
  downloadDocument: (clientId, docId) => api.get(`/clients/${clientId}/documents/${docId}/download`),
};

export const sitesAPI = {
  getAll: (params = {}) => api.get('/sites', { params }),
  getById: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  archive: (id) => api.put(`/sites/${id}/archive`),
  restore: (id) => api.put(`/sites/${id}/restore`),
  delete: (id) => api.delete(`/sites/${id}`),
};

export const assetsAPI = {
  getAll: (siteId, params = {}) => api.get('/assets', { params: { site_id: siteId, ...params } }),
  getById: (id) => api.get(`/assets/${id}`),
  create: (data) => api.post('/assets', data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  update: (id, data) => api.put(`/assets/${id}`, data, { headers: { 'Content-Type': 'multipart/form-data' } }),
  archive: (id) => api.put(`/assets/${id}/archive`),
  restore: (id) => api.put(`/assets/${id}/restore`),
  delete: (id) => api.delete(`/assets/${id}`),
  getTelemetry: (assetId, limit = 100) => api.get(`/telemetry/asset/${assetId}`, { params: { limit } }),
  getMetering: (assetId) => api.get(`/metering/assets/${assetId}`),
  getMonthlyUsage: (assetId, year, month) => api.get(`/metering/assets/${assetId}/monthly/${year}/${month}`),
};

export const siteMeteringAPI = {
  getSiteDetail: (siteId) => api.get(`/sites/${siteId}/detail`),
  getSiteMetering: (siteId) => api.get(`/metering/sites/${siteId}/aggregated`),
  getSiteMonthly: (siteId, year, month) => api.get(`/metering/sites/${siteId}/monthly/${year}/${month}/aggregated`),
  getClientMetering: (clientId) => api.get(`/metering/clients/${clientId}/aggregated`),
  getFleetMetering: (year, month) => api.get(`/metering/fleet/${year}/${month}/aggregated`),
};

export const tariffsAPI = {
  getAll: (clientId) => api.get('/tariffs', { params: { client_id: clientId } }),
  create: (data) => api.post('/tariffs', data),
};

export const billingAPI = {
  getCycles: (clientId, status) => api.get('/billing/cycles', { params: { client_id: clientId, status_filter: status } }),
  createCycle: (data) => api.post('/billing/cycles', data),
  approveCycle: (cycleId) => api.put(`/billing/cycles/${cycleId}/approve`),
  lockCycle: (cycleId) => api.put(`/billing/cycles/${cycleId}/lock`),
  getInvoice: (cycleId) => api.get(`/billing/invoices/${cycleId}`),
};

// New invoicing module (Phase A) — replaces legacy `billingAPI` in the Billing tab + Client tab.
export const invoicingAPI = {
  getSettings: () => api.get('/invoicing/voltide-settings'),
  updateSettings: (patch) => api.put('/invoicing/voltide-settings', patch),

  getBillingProfile: (clientId) => api.get(`/invoicing/clients/${clientId}/billing-profile`),
  updateBillingProfile: (clientId, patch) => api.put(`/invoicing/clients/${clientId}/billing-profile`, patch),

  getTerms: (clientId) => api.get(`/invoicing/clients/${clientId}/commercial-terms`),
  updateTerms: (clientId, patch) => api.put(`/invoicing/clients/${clientId}/commercial-terms`, patch),

  // Phase C — AI Agreement Extract (reference-only)
  getAgreementExtract: (clientId) => api.get(`/invoicing/clients/${clientId}/agreement-extract`),
  getAgreementPrefill: (clientId) => api.get(`/invoicing/clients/${clientId}/agreement-extract/prefill`),
  runAgreementExtractFromDoc: (clientId, docId) => api.post(`/invoicing/clients/${clientId}/agreement-extract/run-from-document/${docId}`),
  runAgreementExtractFromUpload: (clientId, fileFormData) => api.post(
    `/invoicing/clients/${clientId}/agreement-extract/run`,
    fileFormData,
    { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 },
  ),

  listInvoices: (params = {}) => api.get('/invoicing/invoices', { params }),
  getInvoice: (invoiceId) => api.get(`/invoicing/invoices/${invoiceId}`),
  createAdhoc: (data) => api.post('/invoicing/invoices/adhoc', data),
  createMonthly: (data) => api.post('/invoicing/invoices/monthly', data),
  runMonthlyBatch: (year, month) => api.post(`/invoicing/invoices/run-monthly-batch?year=${year}&month=${month}`),
  updateInvoice: (id, patch) => api.put(`/invoicing/invoices/${id}`, patch),
  requestApproval: (id) => api.post(`/invoicing/invoices/${id}/request-approval`),
  approve: (id) => api.post(`/invoicing/invoices/${id}/approve`),
  markPaid: (id) => api.post(`/invoicing/invoices/${id}/mark-paid`),
  voidInvoice: (id, reason) => api.post(`/invoicing/invoices/${id}/void`, { reason }),
  generatePdf: (id) => api.post(`/invoicing/invoices/${id}/generate-pdf`),
  // PDF + preview endpoints require Authorization header → cannot use a plain
  // window.open(url) (browsers don't carry the bearer token across new-tab
  // navigations). Fetch as a blob via the authenticated axios client and open
  // an object URL instead.
  openPdf: async (id) => {
    const r = await api.get(`/invoicing/invoices/${id}/pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
  openPreview: async (id) => {
    const r = await api.get(`/invoicing/invoices/${id}/preview-html`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
  pdfUrl: (id) => `${api.defaults.baseURL}/invoicing/invoices/${id}/pdf`,         // legacy direct-link (auth-required)
  previewUrl: (id) => `${api.defaults.baseURL}/invoicing/invoices/${id}/preview-html`,
  sendEmail: (id, body) => api.post(`/invoicing/invoices/${id}/send-email`, body),
  emailLogs: (invoiceId) => api.get('/invoicing/email-logs', { params: { invoice_id: invoiceId } }),
  wipeTestData: () => api.post('/invoicing/admin/wipe-test-data'),
};

// Twilio WhatsApp integration (Phase 1)
export const whatsappAPI = {
  listContacts: () => api.get('/whatsapp/contacts'),
  upsertContact: (data) => api.post('/whatsapp/contacts', data),
  deleteContact: (id) => api.delete(`/whatsapp/contacts/${id}`),
  defaultPolicy: () => api.get('/whatsapp/default-subscriptions'),
  resolve: (notification_type, site_id) => api.get('/whatsapp/resolve', { params: { notification_type, site_id } }),
  listSubscriptions: (params = {}) => api.get('/whatsapp/subscriptions', { params }),
  upsertSubscription: (data) => api.post('/whatsapp/subscriptions', data),
  sendTest: (data) => api.post('/whatsapp/send-test', data),
  messages: (limit = 100) => api.get('/whatsapp/messages', { params: { limit } }),
  seed: () => api.post('/whatsapp/admin/seed'),
};

export const alarmsAPI = {
  getAll: (siteId, resolved) => api.get('/alarms', { params: { site_id: siteId, resolved } }),
  create: (data) => api.post('/alarms', data),
  acknowledge: (alarmId) => api.put(`/alarms/${alarmId}/acknowledge`),
  resolve: (alarmId, data) => api.put(`/alarms/${alarmId}/resolve`, data),
  // Operational dashboard
  getActive: (siteId) => api.get('/alarms/active', { params: siteId ? { site_id: siteId } : {} }),
  getCounts: () => api.get('/alarms/counts'),
  getHistory: (params = {}) => api.get('/alarms/history', { params }),
  clear: (alarmId, note) => api.post(`/alarms/${alarmId}/clear`, { note }),
  triggerDetect: () => api.post('/alarms/detect'),
};

export const dashboardAPI = {
  // Single source of truth for the Command Center page
  getFleet: () => api.get('/dashboard/fleet'),
};

export const telemetryAPI = {
  getRecords: (siteId, limit = 100) => api.get(`/telemetry/${siteId}`, { params: { limit } }),
  getDailySummaries: (siteId, days = 30) => api.get(`/summaries/daily/${siteId}`, { params: { days } }),
  getMonthlySummaries: (siteId, months = 12) => api.get(`/summaries/monthly/${siteId}`, { params: { months } }),
};

// Consolidated Live Monitoring page (single source of truth per signal)
export const liveMonitoringAPI = {
  getSummary: (siteId) => api.get(`/live-monitoring/sites/${siteId}/summary`),
  getTrend: (siteId, range = 'today') =>
    api.get(`/live-monitoring/sites/${siteId}/trend`, { params: { range } }),
  getEvents: (siteId, limit = 50) =>
    api.get(`/live-monitoring/sites/${siteId}/events`, { params: { limit } }),
};

// Bit23 / Grid outage endpoints
export const outagesAPI = {
  // Live status
  getGridStatus: (siteId) => api.get(`/telemetry/sites/${siteId}/grid-status`),
  getAllGridStatuses: () => api.get('/telemetry/grid-status/all'),
  getBessState: (siteId) => api.get(`/telemetry/sites/${siteId}/bess-state`),

  // Events & summaries
  listOutages: (siteId, params = {}) => api.get(`/telemetry/sites/${siteId}/outages`, { params }),
  getLatestOutage: (siteId) => api.get(`/telemetry/sites/${siteId}/outages/latest`),
  getSummaries: (siteId, months = 6) =>
    api.get(`/telemetry/sites/${siteId}/outages/summary`, { params: { months } }),
  recomputeSummaries: (siteId) =>
    api.post(`/telemetry/sites/${siteId}/outages/recompute-summaries`),

  // Backfill
  triggerBackfill: (siteId, daysBack = 90) =>
    api.post(`/telemetry/sites/${siteId}/bit23/backfill`, { days_back: daysBack }),
  getBackfillStatus: (siteId) =>
    api.get(`/telemetry/sites/${siteId}/bit23/backfill/status`),

  // Regional benchmark
  getRegionalBenchmark: (params = {}) =>
    api.get('/telemetry/outages/regional-benchmark', { params }),

  // DI port discovery
  discoverPorts: (siteId) =>
    api.post(`/telemetry/sites/${siteId}/peripheral-ports/discover`),
  getPorts: (siteId) => api.get(`/telemetry/sites/${siteId}/peripheral-ports`),
};

export const usersAPI = {
  getAll: () => api.get('/users'),
};

export const permissionsAPI = {
  create: (data) => api.post('/permissions', data),
  getUserPermissions: (userId) => api.get(`/permissions/user/${userId}`),
};

export const auditAPI = {
  getLogs: (limit = 100, params = {}) => api.get('/audit-logs', { params: { limit, ...params } }),
  exportCSV: (params = {}) => api.get('/audit-logs/export/csv', { params, responseType: 'blob' }),
  exportJSON: (params = {}) => api.get('/audit-logs/export/json', { params }),
};

export const dataAPI = {
  uploadCSV: (file, siteId) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data/csv-upload', formData, {
      params: { site_id: siteId },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  triggerVendorSync: () => api.get('/data/vendor-sync'),
  createMockTelemetry: (siteId) => api.post(`/data/mock-telemetry/${siteId}`),
};

// ==================== PROSPECT / PRE-SALES APIs ====================

export const prospectsAPI = {
  getAll: (status) => api.get('/prospects', { params: status ? { status } : {} }),
  getById: (id) => api.get(`/prospects/${id}`),
  create: (data) => api.post('/prospects', data),
  update: (id, data) => api.put(`/prospects/${id}`, data),
  updateStatus: (id, status) => {
    const form = new FormData();
    form.append('status', status);
    return api.put(`/prospects/${id}/status`, form);
  },
  delete: (id) => api.delete(`/prospects/${id}`),
  archive: (id) => api.post(`/prospects/${id}/archive`),
  restore: (id) => api.post(`/prospects/${id}/restore`),

  // Sites
  getSites: (prospectId) => api.get(`/prospects/${prospectId}/sites`),
  getSite: (siteId) => api.get(`/prospects/sites/${siteId}`),
  createSite: (prospectId, data) => api.post(`/prospects/${prospectId}/sites`, data),
  updateSite: (siteId, data) => api.put(`/prospects/sites/${siteId}`, data),
  deleteSite: (siteId) => api.delete(`/prospects/sites/${siteId}`),

  // Pipeline
  getPipelineStatus: (siteId) => api.get(`/prospects/sites/${siteId}/pipeline-status`),
  submitRequirement: (siteId, notes = '') => api.post(`/prospects/sites/${siteId}/submit-requirement`, { notes }),
  selectConfiguration: (siteId, data) => api.post(`/prospects/sites/${siteId}/select-configuration`, data),
  deselectConfiguration: (siteId) => api.post(`/prospects/sites/${siteId}/deselect-configuration`),
  vendorMatch: (siteId) => api.post(`/prospects/sites/${siteId}/vendor-match`),
  saveManualConfig: (siteId, data) => api.post(`/prospects/sites/${siteId}/manual-config`, data),
  listManualConfigs: (siteId) => api.get(`/prospects/sites/${siteId}/manual-configs`),
  requestApproval: (siteId, notes = '') => api.post(`/prospects/sites/${siteId}/request-approval`, { notes }),
  approveProposal: (siteId, notes = '') => api.post(`/prospects/sites/${siteId}/approve-proposal`, { notes }),
  sendProposal: (siteId) => api.post(`/prospects/sites/${siteId}/send-proposal`),
  // Legacy compat
  lockConfiguration: (siteId, configRunId, selectedOptionId) =>
    api.post(`/prospects/sites/${siteId}/lock-configuration`, { config_run_id: configRunId, selected_option_id: selectedOptionId }),

  // Requirements
  createRequirement: (siteId, data) => api.post(`/prospects/sites/${siteId}/requirements`, data),
  upsertRequirement: (siteId, data) => api.put(`/prospects/sites/${siteId}/requirement`, data),
  updateRequirement: (id, data) => api.put(`/prospects/requirements/${id}`, data),
  deleteRequirement: (id) => api.delete(`/prospects/requirements/${id}`),

  // DG Assets
  createDGAsset: (siteId, data) => api.post(`/prospects/sites/${siteId}/dg-assets`, data),
  updateDGAsset: (id, data) => api.put(`/prospects/dg-assets/${id}`, data),
  deleteDGAsset: (id) => api.delete(`/prospects/dg-assets/${id}`),

  // Files
  uploadFile: (siteId, file, fileType) => {
    const form = new FormData();
    form.append('file', file);
    form.append('file_type', fileType);
    return api.post(`/prospects/sites/${siteId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getFiles: (siteId) => api.get(`/prospects/sites/${siteId}/files`),
  deleteFile: (id) => api.delete(`/prospects/files/${id}`),

  // Bills
  createBill: (data) => api.post('/prospects/bills', data),
  getSiteBills: (siteId) => api.get(`/prospects/sites/${siteId}/bills`),
  updateBill: (id, data) => api.put(`/prospects/bills/${id}`, data),
  approveBill: (id) => api.put(`/prospects/bills/${id}/approve`),
  deleteBill: (id) => api.delete(`/prospects/bills/${id}`),

  // DG Logs
  createDGLog: (data) => api.post('/prospects/dg-logs', data),
  getSiteDGLogs: (siteId) => api.get(`/prospects/sites/${siteId}/dg-logs`),
  updateDGLog: (id, data) => api.put(`/prospects/dg-logs/${id}`, data),
  approveDGLog: (id) => api.put(`/prospects/dg-logs/${id}/approve`),
  deleteDGLog: (id) => api.delete(`/prospects/dg-logs/${id}`),
  batchSaveDGRows: (siteId, rows) => api.post(`/prospects/sites/${siteId}/dg-digitise`, { rows }),
  getDGMonthlySummary: (siteId) => api.get(`/prospects/sites/${siteId}/dg-monthly-summary`),
  parseDGLogFile: (uploadId) => api.post(`/prospects/files/${uploadId}/parse-dg-log`),

  // Config Drafts
  listConfigDrafts: (siteId) => api.get(`/prospects/sites/${siteId}/config-drafts`),
  saveConfigDraft: (siteId, data) => api.post(`/prospects/sites/${siteId}/config-drafts`, data),
  updateConfigDraft: (siteId, draftId, data) => api.put(`/prospects/sites/${siteId}/config-drafts/${draftId}`, data),
  duplicateConfigDraft: (siteId, draftId, newName) => api.post(`/prospects/sites/${siteId}/config-drafts/${draftId}/duplicate`, { new_name: newName }),
  deleteConfigDraft: (siteId, draftId) => api.delete(`/prospects/sites/${siteId}/config-drafts/${draftId}`),

  // Intake Links
  generateIntakeLink: (siteId, hours = 72) => {
    const form = new FormData();
    form.append('hours', hours);
    return api.post(`/prospects/sites/${siteId}/intake-link`, form);
  },
  getIntakeLinks: (siteId) => api.get(`/prospects/sites/${siteId}/intake-links`),
  revokeIntakeLink: (linkId) => api.put(`/prospects/intake-links/${linkId}/revoke`),
};


// ==================== CONFIGURATOR APIs ====================

export const configuratorAPI = {
  run: (requirementId) => api.post('/configurator/run', { requirement_id: requirementId }),
  getRuns: (params) => api.get('/configurator/runs', { params }),
  getRun: (runId) => api.get(`/configurator/runs/${runId}`),
  selectOption: (runId, optionId) => api.post(`/configurator/runs/${runId}/select`, { option_id: optionId }),
};

// ==================== PRICING MASTER APIs ====================

export const pricingAPI = {
  // Vendors
  getVendors: (activeOnly = true) => api.get('/pricing/vendors', { params: { active_only: activeOnly } }),
  createVendor: (data) => api.post('/pricing/vendors', data),
  updateVendor: (id, data) => api.put(`/pricing/vendors/${id}`, data),
  archiveVendor: (id) => api.delete(`/pricing/vendors/${id}`),

  // PCS Master
  getPCS: (vendorId) => api.get('/pricing/pcs', { params: vendorId ? { vendor_id: vendorId } : {} }),
  createPCS: (data) => api.post('/pricing/pcs', data),
  updatePCS: (id, data) => api.put(`/pricing/pcs/${id}`, data),
  archivePCS: (id) => api.delete(`/pricing/pcs/${id}`),

  // Battery Master
  getBatteries: (vendorId) => api.get('/pricing/batteries', { params: vendorId ? { vendor_id: vendorId } : {} }),
  createBattery: (data) => api.post('/pricing/batteries', data),
  updateBattery: (id, data) => api.put(`/pricing/batteries/${id}`, data),
  archiveBattery: (id) => api.delete(`/pricing/batteries/${id}`),

  // System SKUs
  getSystems: (vendorId) => api.get('/pricing/systems', { params: vendorId ? { vendor_id: vendorId } : {} }),
  createSystem: (data) => api.post('/pricing/systems', data),
  updateSystem: (id, data) => api.put(`/pricing/systems/${id}`, data),
  archiveSystem: (id) => api.delete(`/pricing/systems/${id}`),

  // Pricing Matrix
  getMatrix: (params) => api.get('/pricing/matrix', { params: { active_only: false, ...params } }),
  createMatrix: (data) => api.post('/pricing/matrix', data),
  updateMatrix: (id, data) => api.put(`/pricing/matrix/${id}`, data),
  toggleMatrix: (id) => api.put(`/pricing/matrix/${id}/toggle`),
  deleteMatrix: (id) => api.delete(`/pricing/matrix/${id}`),

  // Pricing Lookup
  lookup: (pcsPowerKw, batteryKwh, vendorId) =>
    api.get('/pricing/lookup', { params: { pcs_power_kw: pcsPowerKw, battery_kwh: batteryKwh, vendor_id: vendorId } }),

  // Import
  importPreview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/pricing/import/preview', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  importApprove: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/pricing/import/approve', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

// ==================== CATALOG APIs ====================

export const catalogAPI = {
  // Voltide SKU Sheet
  syncSheet: () => api.post('/catalog/sheet/sync'),
  getSheet: () => api.get('/catalog/sheet'),
  updateSheetRow: (id, data) => api.put(`/catalog/sheet/${id}`, data),
  bulkSheetAction: (data) => api.post('/catalog/sheet/bulk', data),
  recomputeRent: (id) => api.post(`/catalog/sheet/${id}/recompute-rent`),

  // Rental Pricing Defaults
  getRentalDefaults: () => api.get('/catalog/rental-defaults'),
  createRentalDefaults: (data) => api.post('/catalog/rental-defaults', data),
  updateRentalDefaults: (id, data) => api.put(`/catalog/rental-defaults/${id}`, data),

  // Rental Snapshots
  getRentalSnapshots: (skuId, statusFilter) => api.get('/catalog/rental-snapshots', { params: { ...(skuId && { sku_id: skuId }), ...(statusFilter && { status_filter: statusFilter }) } }),

  // Website products (public)
  getWebsiteProducts: () => api.get('/catalog/website/products'),

  // Audit
  getSheetAudit: (rowId) => api.get(`/catalog/sheet-audit/${rowId}`),
};

// ==================== RENTAL CALCULATOR APIs ====================

export const rentalCalcAPI = {
  compute: (inputs, levers) => api.post('/rental-calc/compute', { inputs, levers }),
  create: (data) => api.post('/rental-calc/', data),
  list: (params) => api.get('/rental-calc/', { params }),
  get: (id) => api.get(`/rental-calc/${id}`),
  autoPopulate: (prospectSiteId) => api.get(`/rental-calc/auto-populate/${prospectSiteId}`),
  pricingMatrixSearch: () => api.get('/rental-calc/pricing-matrix-search'),
  updateInputs: (id, inputs, levers) => api.put(`/rental-calc/${id}/inputs`, { inputs, levers }),
  submit: (id) => api.post(`/rental-calc/${id}/submit`),
  updateLevers: (id, levers) => api.put(`/rental-calc/${id}/levers`, { levers }),
  approve: (id, action, comments) => api.post(`/rental-calc/${id}/approve`, { action, comments }),
  lock: (id) => api.post(`/rental-calc/${id}/lock`),
};

// ==================== PROPOSAL APIs ====================
// V1 proposal API has been removed — use proposalV2API (defined below) instead.

// ==================== REPORTS APIs ====================
export const reportsAPI = {
  getDailySales: () => api.get('/reports/daily-sales'),
  sendDailySales: () => api.post('/reports/daily-sales/send'),
  getSubscriptions: () => api.get('/reports/subscriptions'),
  createSubscription: (data) => api.post('/reports/subscriptions', data),
  updateSubscription: (id, data) => api.put(`/reports/subscriptions/${id}`, data),
  deleteSubscription: (id) => api.delete(`/reports/subscriptions/${id}`),
  getEmailLogs: (reportType, limit) => api.get('/reports/email-logs', { params: { report_type: reportType, limit } }),
};

// ==================== TELEMETRY TESTING APIs ====================
export const telemetryTestAPI = {
  testLink: (assetId) => api.post(`/assets/${assetId}/test-telemetry`),
};

// ==================== JEM INTEGRATION APIs ====================
export const jemAPI = {
  // Auth
  setToken: (token, hours) => api.post('/jem/set-token', { token, hours }),
  authStatus: () => api.get('/jem/auth-status'),
  // Stations
  syncStations: () => api.post('/jem/stations/sync'),
  getStations: () => api.get('/jem/stations'),
  // Systems
  syncSystems: (stationId) => api.post(`/jem/stations/${stationId}/systems/sync`),
  getSystems: (stationId) => api.get(`/jem/stations/${stationId}/systems`),
  // Mappings
  getMappings: () => api.get('/jem/mappings'),
  createMapping: (data) => api.post('/jem/mappings', data),
  deleteMapping: (id) => api.delete(`/jem/mappings/${id}`),
  toggleMapping: (id) => api.post(`/jem/mappings/${id}/toggle`),
  // Polling
  pollAsset: (assetId) => api.post(`/jem/poll/${assetId}`),
  getPollStatuses: () => api.get('/jem/poll-status'),
  startPolling: () => api.post('/jem/polling/start'),
  stopPolling: () => api.post('/jem/polling/stop'),
  pollingStatus: () => api.get('/jem/polling/status'),
  setInterval: (minutes) => api.put('/jem/polling/interval', { minutes }),
  // Site-level
  linkStation: (siteId, data) => api.post(`/jem/sites/${siteId}/link-station`, data),
  unlinkStation: (siteId) => api.post(`/jem/sites/${siteId}/unlink-station`),
  createAssetFromSystem: (data) => api.post('/jem/assets/create-from-system', data),
  getSiteMappings: (siteId) => api.get('/jem/mappings', { params: { site_id: siteId } }),
  getLatestTelemetry: (assetId) => api.get(`/jem/telemetry/${assetId}/latest`),
  // Peripheral config (Bit23)
  getPeripheralConfig: (siteId) => api.get(`/jem/sites/${siteId}/peripheral-config`),
  updatePeripheralConfig: (siteId, data) => api.patch(`/jem/sites/${siteId}/peripheral-config`, data),
  // Raw-mirror ingestion (auto-discovered devices)
  getDevices: (siteId) => api.get(`/jem/sites/${siteId}/devices`),
  syncDevices: (siteId) => api.post(`/jem/sites/${siteId}/devices/sync`),
  mirrorNow: (siteId) => api.post(`/jem/sites/${siteId}/mirror-now`),
  getRawSamples: (siteId, params = {}) => api.get(`/jem/sites/${siteId}/raw-samples`, { params }),
  getLatestPayload: (siteId, deviceId) => api.get(`/jem/sites/${siteId}/devices/${deviceId}/latest-payload`),
  getFieldCatalog: (deviceType, refresh = false) => api.get(`/jem/field-catalog/${deviceType}`, { params: { refresh } }),
  setGridSource: (siteId, data) => api.patch(`/jem/sites/${siteId}/grid-source`, data),
  getGridSourceConflicts: (siteId) => api.get(`/jem/sites/${siteId}/grid-source-conflicts`),
  startGridBackfill: (siteId, daysBack = 30) => api.post(`/jem/sites/${siteId}/grid-backfill`, { days_back: daysBack }),
  gridBackfillStatus: (siteId) => api.get(`/jem/sites/${siteId}/grid-backfill/status`),
  // Site-level telemetry
  getSiteLiveTelemetry: (siteId) => api.get(`/jem/sites/${siteId}/live-telemetry`),
  getSitePowercuts: (siteId, limit = 50) => api.get(`/jem/sites/${siteId}/powercuts`, { params: { limit } }),
};

// ==================== MIGRATION APIs ====================
export const migrationAPI = {
  backfillLinks: (dryRun = true) => api.post('/migrations/backfill-links-v1', null, { params: { dry_run: dryRun } }),
  validateLinks: () => api.post('/migrations/validate-links'),
};

export default api;

// ==================== VERIFICATION QUEUE APIs ====================
export const verificationAPI = {
  getQueue: (status) => api.get('/verification-queue', { params: status ? { status } : {} }),
  getFileDetail: (uploadId) => api.get(`/verification-queue/files/${uploadId}/detail`),
  processFile: (uploadId) => api.put(`/verification-queue/files/${uploadId}/process`),
  approveFile: (uploadId) => api.put(`/verification-queue/files/${uploadId}/approve`),
  rejectFile: (uploadId, reason) => api.put(`/verification-queue/files/${uploadId}/reject`, { reason }),
  correctFile: (uploadId, extractedData) => api.put(`/verification-queue/files/${uploadId}/correct`, { extracted_data: extractedData }),
  extractFile: (uploadId) => api.post(`/verification-queue/files/${uploadId}/extract`),
  splitBills: (uploadId, splits) => api.post(`/prospects/uploads/${uploadId}/split-bills`, { splits }),
};


// ==================== RENTAL SANDBOX APIs ====================
export const rentalSandboxAPI = {
  listRuns: () => api.get('/rental-sandbox/runs'),
  createRun: (data) => api.post('/rental-sandbox/runs', data),
  getRun: (runId) => api.get(`/rental-sandbox/runs/${runId}`),
  updateRun: (runId, data) => api.put(`/rental-sandbox/runs/${runId}`, data),
  deleteRun: (runId) => api.delete(`/rental-sandbox/runs/${runId}`),
  duplicateRun: (runId) => api.post(`/rental-sandbox/runs/${runId}/duplicate`),
  createScenario: (runId, data) => api.post(`/rental-sandbox/runs/${runId}/scenarios`, data),
  deleteScenario: (runId, scenarioId) => api.delete(`/rental-sandbox/runs/${runId}/scenarios/${scenarioId}`),
  compare: (runId) => api.get(`/rental-sandbox/runs/${runId}/compare`),
  promote: (runId, data) => api.post(`/rental-sandbox/runs/${runId}/promote`, data),
  aiSimulate: (data) => api.post('/rental-sandbox/ai-simulate', data),
  aiApply: (data) => api.post('/rental-sandbox/ai-simulate/apply', data),
  getPresets: () => api.get('/rental-sandbox/presets'),
  createPreset: (data) => api.post('/rental-sandbox/presets', data),
  updatePreset: (presetId, data) => api.put(`/rental-sandbox/presets/${presetId}`, data),
  deletePreset: (presetId) => api.delete(`/rental-sandbox/presets/${presetId}`),
  savePresetFromRun: (runId, data) => api.post(`/rental-sandbox/presets/save-from-run/${runId}`, data),
};

// ==================== CONFIGURATOR SANDBOX APIs ====================
export const configSandboxAPI = {
  run: (data) => api.post('/configurator-sandbox/run', data),
  listRuns: () => api.get('/configurator-sandbox/runs'),
  getRun: (runId) => api.get(`/configurator-sandbox/runs/${runId}`),
  deleteRun: (runId) => api.delete(`/configurator-sandbox/runs/${runId}`),
  compare: (scenarios) => api.post('/configurator-sandbox/compare', { scenarios }),
};

// ==================== INBOUND RFQ APIs ====================
export const rfqAPI = {
  list: (status) => api.get('/rfq', { params: status ? { status } : {} }),
  getCounts: () => api.get('/rfq/counts'),
  getDetail: (rfqId) => api.get(`/rfq/${rfqId}`),
  getSalesUsers: () => api.get('/rfq/meta/sales-users'),
  assignOwner: (rfqId, ownerUserId) => api.put(`/rfq/${rfqId}/assign`, { owner_user_id: ownerUserId }),
  markUnderReview: (rfqId, notes = '') => api.post(`/rfq/${rfqId}/under-review`, { notes }),
  requestMoreInfo: (rfqId, data) => api.post(`/rfq/${rfqId}/request-info`, data),
  reject: (rfqId, data) => api.post(`/rfq/${rfqId}/reject`, data),
  approveConvert: (rfqId, data) => api.post(`/rfq/${rfqId}/approve-convert`, data),
  getAudit: (rfqId) => api.get(`/rfq/${rfqId}/audit`),
};


// ==================== V2 PROPOSAL ENGINE APIs ====================
export const proposalV2API = {
  listTemplates: () => api.get('/v2/proposals/templates'),
  getTemplate: (templateId) => api.get(`/v2/proposals/templates/${templateId}`),
  create: (templateId, payload) => api.post('/v2/proposals/', { template_id: templateId, payload }),
  list: () => api.get('/v2/proposals/'),
  get: (proposalId) => api.get(`/v2/proposals/${proposalId}`),
  update: (proposalId, payload) => api.put(`/v2/proposals/${proposalId}`, { payload }),
  renderPdf: (proposalId) => api.post(`/v2/proposals/${proposalId}/render`, null, { responseType: 'blob' }),
  previewUrl: (proposalId) => `/v2/proposals/${proposalId}/preview`,
  listEligibleSites: () => api.get('/v2/proposals/prospect-sites/eligible'),
  createFromSite: (templateId, prospectSiteId) => api.post('/v2/proposals/from-prospect-site', { template_id: templateId, prospect_site_id: prospectSiteId }),
};

// ==================== CONFIG SUMMARY APIs ====================
export const configSummaryAPI = {
  getOverview: () => api.get('/config-summary/overview'),
};

// ==================== SALES AGENT APIs ====================
export const salesAgentAPI = {
  chat: (message, sessionId) => api.post('/sales-agent/chat', { message, session_id: sessionId }),
  processEmail: (emailText) => api.post('/sales-agent/process-email', { email_text: emailText }),
  getSessions: (limit = 20) => api.get('/sales-agent/sessions', { params: { limit } }),
  getSessionMessages: (sessionId, limit = 50) => api.get(`/sales-agent/sessions/${sessionId}/messages`, { params: { limit } }),
  getInboundLogs: (limit = 20) => api.get('/sales-agent/inbound-logs', { params: { limit } }),
};

export const reportingAPI = {
  generateCustom: (siteId, body) => api.post(`/reporting/sites/${siteId}/generate-custom`, body),
  listReports: (siteId, params = {}) => api.get(`/reporting/sites/${siteId}/reports`, { params }),
  openPdf: async (reportId) => {
    const r = await api.get(`/reporting/reports/${reportId}/pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
  openPreview: async (reportId) => {
    const r = await api.get(`/reporting/reports/${reportId}/preview-html`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  },
  sendEmail: (reportId, body) => api.post(`/reporting/reports/${reportId}/send-email`, body),
};

export const dieselPricingAPI = {
  list: () => api.get('/diesel-prices'),
  upsert: (data) => api.post('/diesel-prices', data),
  remove: (state, month) => api.delete(`/diesel-prices/${state}/${month}`),
};

// ==================== AGENT FILE UPLOAD APIs ====================
export const agentUploadAPI = {
  upload: (formData) => api.post('/agent-uploads/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  get: (uploadId) => api.get(`/agent-uploads/${uploadId}`),
  listByOwner: (ownerType, ownerId) => api.get(`/agent-uploads/by-owner/${ownerType}/${ownerId}`),
  attach: (uploadId, ownerType, ownerId) => {
    const fd = new FormData();
    fd.append('owner_type', ownerType);
    fd.append('owner_id', ownerId);
    return api.post(`/agent-uploads/${uploadId}/attach`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  applyToProspect: (uploadId, prospectId) => {
    const fd = new FormData();
    fd.append('prospect_id', prospectId);
    return api.post(`/agent-uploads/${uploadId}/apply-prospect`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  applyToDg: (uploadId, prospectSiteId) => {
    const fd = new FormData();
    fd.append('prospect_site_id', prospectSiteId);
    return api.post(`/agent-uploads/${uploadId}/apply-dg`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  fileUrl: (uploadId) => `${API_BASE}/agent-uploads/${uploadId}/file`,
};

export const siteAnalysisAPI = {
  create: (siteId) => api.post(`/analysis/sites/${siteId}/create`),
  get: (analysisId) => api.get(`/analysis/${analysisId}`),
  listForSite: (siteId) => api.get(`/analysis/sites/${siteId}`),
  listSources: (siteId) => api.get(`/analysis/sites/${siteId}/sources`),
  extractFromVault: (analysisId, uploadIds) =>
    api.post(`/analysis/${analysisId}/extract-from-vault`, { upload_ids: uploadIds }),
  uploadBill: (analysisId, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/analysis/${analysisId}/upload-bill`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  saveBillData: (analysisId, billData) => api.put(`/analysis/${analysisId}/bill-data`, { bill_data: billData }),
  validateBills: (analysisId, billData) => api.post(`/analysis/${analysisId}/validate-bills`, { bill_data: billData }),
  updateSiteCtx: (analysisId, patch) => api.put(`/analysis/${analysisId}/site-ctx`, patch),
  detectIndustry: (analysisId, payload) => api.post(`/analysis/${analysisId}/detect-industry`, payload),
  confirm: (analysisId) => api.post(`/analysis/${analysisId}/confirm`),
  generateReport: (analysisId) => api.post(`/analysis/${analysisId}/generate-report`),
  reportUrl: (analysisId) => `${API_BASE}/analysis/${analysisId}/report.pdf`,  // raw URL (auth-required) — prefer openReport()
  openReport: async (analysisId) => {
    // Fetch via axios so the Authorization header is attached, then surface
    // the PDF via a blob URL in a new tab. Mirrors invoicingAPI.previewPdf.
    const r = await api.get(`/analysis/${analysisId}/report.pdf`, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    window.open(blobUrl, '_blank');
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    return blobUrl;
  },
  delete: (analysisId) => api.delete(`/analysis/${analysisId}`),
};

// Update salesAgentAPI.chat to support upload_ids
const _origSalesChat = salesAgentAPI.chat;
salesAgentAPI.chat = (message, sessionId, uploadIds) =>
  api.post('/sales-agent/chat', { message, session_id: sessionId, upload_ids: uploadIds });

